import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Loader from "components/Layout/Loader";
import { saveAs } from "file-saver";
import {
  downloadMembershipReceipt,
  fetchEmailVerificationStatus,
  fetchFreeVsPremium,
  fetchMembershipConfirmation,
  handelSpeakerEmailVerify,
  showSuccess,
} from "store/slices";
import "./MembershipConfirmed.scss";

const LINKEDIN_COMMUNITY_URL = "https://www.linkedin.com/groups/19049029/";
const SPEAKER_BUREAU_EMAIL = "speakerbureau@iferp.in";
const STATUS_POLL_MS = 10000;

const services = [
  {
    key: "speaker",
    icon: "🎤",
    tone: "blue",
    title: "Become a Speaker",
    text: "Present your published work to a global audience and build your international profile.",
    cta: "Apply to speak",
  },
  {
    key: "listener",
    icon: "🎧",
    tone: "orange",
    title: "Become a Listener",
    text: "Attend sessions as a delegate, join the Q&A, and earn participation certificates.",
    cta: "Register as listener",
    href: "https://www.iferp.in/upcoming-international-conference.php",
  },
  {
    key: "editing",
    icon: "📝",
    tone: "pink",
    title: "Research Article Editing",
    text: "Expert editing to help you get published in top-tier, indexed journals.",
    cta: "Learn more",
    href: "https://www.iferp.in/author-services/research-editing",
  },
  {
    key: "proofreading",
    icon: "🔍",
    tone: "cyan",
    title: "Editing & Proofreading",
    text: "Language and structure review to strengthen your ideas and make them land.",
    cta: "Learn more",
    href: "https://www.iferp.in/author-services/editing-proofreading",
  },
];

// contact details confirmed by the IFERP team (#1489)
const concierge = { phone: "+1 325 666 5006", email: "info@iferpmembership.in" };
const support = { phone: "+1 626 649 4484", email: "helpdesk@iferp.in" };
const PRIVACY_URL = "https://www.iferp.in/privacy-policy.php";
// refund policy is part of the terms of service
const TERMS_URL = "https://www.iferp.in/terms-and-conditions.php";

const conferenceTones = [
  { color: "slate", icon: "🧠" },
  { color: "orange", icon: "⚙️" },
  { color: "blue", icon: "🏛️" },
];

const formatDate = (date) => (date ? moment(date).format("DD MMM YYYY") : "");
const formatMoney = (amount, currency) =>
  `${currency === "USD" ? "$" : "₹"}${Number(amount || 0).toLocaleString(
    currency === "USD" ? "en-US" : "en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  )}`;

// "Professional-Premium" -> "Premium Professional", "Institution-Gold" -> "Institution Gold"
const planLabel = (planName = "") => {
  const [base, tier] = planName.split("-");
  if (!tier) return base;
  return tier.toLowerCase() === "premium" ? `Premium ${base}` : `${base} ${tier}`;
};

// "IC2TARECD-2026" -> "IC2TARECD 2026", "I2CRET" (Sep 2026) -> "I2CRET 2026"
const conferenceCode = (shortName = "", startDate) => {
  const match = shortName.match(/^(.*?)[\s-]*(\d{4})$/);
  if (match) return `${match[1]} ${match[2]}`;
  return `${shortName} ${moment(startDate).format("YYYY")}`.trim();
};

const conferenceDates = (start, end) => {
  const s = moment(start);
  const e = moment(end || start);
  if (s.isSame(e, "day")) return s.format("DD MMM YYYY");
  if (s.isSame(e, "month")) return `${s.format("DD")}–${e.format("DD MMM YYYY")}`;
  return `${s.format("DD MMM")} – ${e.format("DD MMM YYYY")}`;
};

const Carousel = ({ label, children }) => {
  const trackRef = useRef(null);
  const scroll = (direction) => {
    const track = trackRef.current;
    if (track) {
      track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
    }
  };
  return (
    <>
      <div className="mc-carousel-nav" aria-label={`${label} navigation`}>
        <button type="button" aria-label={`Previous ${label}`} onClick={() => scroll(-1)}>
          ←
        </button>
        <button type="button" aria-label={`Next ${label}`} onClick={() => scroll(1)}>
          →
        </button>
      </div>
      <div className="mc-track" ref={trackRef}>
        {children}
      </div>
    </>
  );
};

const MembershipConfirmed = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { memberType } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isSpeakerOpen, setIsSpeakerOpen] = useState(false);
  const [checkHint, setCheckHint] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [isComparisonLoading, setIsComparisonLoading] = useState(false);
  const [isReceiptLoading, setIsReceiptLoading] = useState(false);
  const [contact, setContact] = useState(null); // "concierge" | "support"

  const loadData = useCallback(async () => {
    const response = await dispatch(fetchMembershipConfirmation());
    if (response?.status === 200) {
      setData(response.data);
    }
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isVerified = !!data?.member?.is_email_verified;

  // pick up a verification done on another device (e.g. the link opened on a phone)
  const refreshVerification = useCallback(async () => {
    const response = await dispatch(fetchEmailVerificationStatus());
    if (response?.status === 200 && response.data?.is_email_verified) {
      setData((prev) =>
        prev
          ? {
              ...prev,
              member: {
                ...prev.member,
                is_email_verified: true,
                member_id: response.data.member_id,
              },
            }
          : prev
      );
      return true;
    }
    return false;
  }, [dispatch]);

  useEffect(() => {
    if (!data || isVerified) return undefined;
    const timer = setInterval(refreshVerification, STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [data, isVerified, refreshVerification]);

  const handleVerifyClick = async () => {
    const verified = await refreshVerification();
    setCheckHint(!verified);
  };

  // loaded only when a free member opens the comparison
  const handleCompareClick = async () => {
    setIsComparisonOpen(true);
    if (comparison || isComparisonLoading) return;
    setIsComparisonLoading(true);
    const response = await dispatch(fetchFreeVsPremium());
    if (response?.status === 200) {
      setComparison(response.data);
    }
    setIsComparisonLoading(false);
  };

  const handleDownloadReceipt = async () => {
    if (isReceiptLoading) return;
    setIsReceiptLoading(true);
    const file = await dispatch(downloadMembershipReceipt());
    if (file) {
      saveAs(file, `IFERP-membership-receipt-${payment?.order_id || payment?.transaction_id || ""}.pdf`);
    }
    setIsReceiptLoading(false);
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (isResending) return;
    setIsResending(true);
    const response = await dispatch(
      handelSpeakerEmailVerify({ email: data?.member?.email_id })
    );
    if (response?.status === 200) {
      dispatch(showSuccess(response?.message));
    }
    setIsResending(false);
  };

  if (isLoading) {
    return (
      <div className="membership-confirmed mc-loading">
        <Loader />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="membership-confirmed mc-loading">
        <p>We couldn't load your membership details.</p>
        <button type="button" className="mc-btn mc-btn-primary" onClick={() => navigate(`/${memberType}/dashboard`)}>
          Go to dashboard
        </button>
      </div>
    );
  }

  const { member, plan, payment, upcoming_conferences: conferences = [] } = data;
  const isFree = plan?.is_free;
  const interest = member?.research_interest;
  const memberIdText = member?.member_id || "Issued after email verification";
  const typeText = isFree ? "Free Member" : planLabel(plan?.plan_name);
  const tierText = isFree ? "Free Plan" : planLabel(plan?.plan_name);
  const validityText = isFree ? "Lifetime, free tier" : `${plan?.validity_months} months`;
  const dashboardPath = `/${memberType}/dashboard`;
  const upgradePath = `/${memberType}/upgrade`;

  return (
    <div className="membership-confirmed">
      <header className="mc-topbar">
        <div className="mc-container mc-topbar-inner">
          <div className="mc-logo" aria-label="IFERP">
            <span className="mc-logo-word">IFERP</span>
            <span className="mc-logo-tag">Research • Publish • Grow</span>
          </div>
          <span className="mc-secure">🔒 Secure registration</span>
        </div>
      </header>

      <section className="mc-hero">
        <div className="mc-container mc-hero-grid">
          <div className="mc-hero-copy">
            <p className="mc-eyebrow">IFERP Membership · {tierText}</p>
            <h1>
              Membership confirmed,
              <br />
              {member?.full_name}
            </h1>
            <p className="mc-hero-text">
              {isFree ? (
                "Your registration is complete. "
              ) : (
                <>
                  Your payment is complete ({formatMoney(payment?.total_paid, payment?.currency)}) and {tierText} is reserved for {plan?.validity_months} months.{" "}
                </>
              )}
              {isVerified
                ? "Your email is verified and your membership ID is ready. Open your dashboard"
                : "Verify your email to collect your membership ID and open your dashboard"}
              {interest ? (
                <>
                  {" "}for <strong className="mc-underline">{interest}</strong>.
                </>
              ) : (
                "."
              )}
            </p>

            {isVerified ? (
              <div className="mc-verified">
                <span className="mc-verified-badge">✓ Email verified</span>
                <button type="button" className="mc-btn mc-btn-primary" onClick={() => navigate(dashboardPath)}>
                  Go to dashboard
                </button>
              </div>
            ) : (
              <>
                <button type="button" className="mc-btn mc-btn-primary mc-btn-verify" onClick={handleVerifyClick}>
                  Verify email &amp; collect membership ID
                </button>
                {checkHint && (
                  <p className="mc-hint" role="status">
                    Open the email we sent to <strong>{member?.email_id}</strong> and click the link. Your membership ID will appear here.
                  </p>
                )}
                <p className="mc-sent">
                  Verification link sent to <strong>{member?.email_id}</strong> · valid {member?.verification_link_validity_hours} hours ·{" "}
                  <a href="#resend" onClick={handleResend} aria-disabled={isResending}>
                    {isResending ? "sending…" : "resend"}
                  </a>
                </p>
              </>
            )}
          </div>

          <div className="mc-hero-side">
            <p className="mc-confirmed">
              <span className="mc-dot" aria-hidden="true" />
              Registration confirmed
            </p>
            <div className="mc-details">
              <p className="mc-details-title">Membership details</p>
              <dl>
                <div>
                  <dt>Membership ID</dt>
                  <dd className={member?.member_id ? "mc-mono" : "mc-pending"}>{memberIdText}</dd>
                </div>
                <div>
                  <dt>Type</dt>
                  <dd>{typeText}</dd>
                </div>
                <div>
                  <dt>Registered</dt>
                  <dd className="mc-mono">{formatDate(plan?.registered_on)}</dd>
                </div>
                <div>
                  <dt>Expires</dt>
                  <dd className={isFree ? "" : "mc-mono"}>{isFree ? "No expiry" : formatDate(plan?.expires_on)}</dd>
                </div>
                <div>
                  <dt>Validity</dt>
                  <dd>{validityText}</dd>
                </div>
              </dl>
              <p className="mc-details-note">
                Complete your profile to get your ID card.
                {isFree && (
                  <>
                    {" "}
                    <button type="button" className="mc-inline-link" onClick={() => navigate(upgradePath)}>
                      Upgrade to Premium membership
                    </button>{" "}
                    to get your certificate.
                  </>
                )}
              </p>
            </div>
            <p className="mc-side-foot">Get yours from your membership dashboard!</p>
          </div>
        </div>
      </section>

      <main className="mc-body">
        <div className="mc-container mc-sections">
          <section className="mc-section">
            <div className="mc-section-head">
              <div>
                <h2>{isVerified ? "Get started with IFERP" : "While you wait for that email"}</h2>
                <p>One step that needs no sign-in, and puts you in front of researchers in your field today.</p>
              </div>
            </div>
            <div className="mc-card mc-community">
              <div className="mc-community-copy">
                <p className="mc-label mc-label-teal">Open to all members</p>
                <h3>IFERP Global Research Community</h3>
                <p>
                  Introduce your work, find collaborators, and follow discussions
                  {interest ? (
                    <>
                      {" "}in <strong>{interest}</strong>
                    </>
                  ) : (
                    " in your field"
                  )}{" "}
                  alongside members across 90+ countries.
                </p>
                <ul className="mc-chips">
                  <li>Peer introductions</li>
                  <li>Collaboration calls</li>
                  <li>Call-for-paper alerts</li>
                </ul>
              </div>
              <a className="mc-btn mc-btn-linkedin" href={LINKEDIN_COMMUNITY_URL} target="_blank" rel="noopener noreferrer">
                Join on LinkedIn
              </a>
            </div>
          </section>

          {conferences.length > 0 && (
            <section className="mc-section">
              <div className="mc-section-head">
                <div>
                  <h2>Upcoming conferences</h2>
                  {interest && conferences.some((c) => c.badge === "Matches your interest") ? (
                    <p>
                      Starting with the ones closest to <strong>{interest}</strong>.
                    </p>
                  ) : (
                    <p>The next IFERP conferences on the calendar.</p>
                  )}
                </div>
              </div>
              <Carousel label="conferences">
                {conferences.map((conference, index) => {
                  const tone = conferenceTones[index % conferenceTones.length];
                  return (
                    <article className="mc-card mc-conference" key={conference.id}>
                      <div className={`mc-conference-cover mc-tone-${tone.color}`}>
                        <span className="mc-cover-icon" aria-hidden="true">{tone.icon}</span>
                        {conference.badge && <span className="mc-badge">{conference.badge}</span>}
                      </div>
                      <div className="mc-conference-body">
                        <p className="mc-code">{conferenceCode(conference.short_name, conference.start_date)}</p>
                        <h3>{conference.name}</h3>
                        <ul className="mc-meta">
                          <li><span aria-hidden="true">📅</span>{conferenceDates(conference.start_date, conference.end_date)}</li>
                          <li>
                            <span aria-hidden="true">📍</span>
                            {[conference.city, conference.country].filter(Boolean).join(", ")}
                            {conference.mode ? ` · ${conference.mode}` : ""}
                          </li>
                          {conference.abstract_deadline && (
                            <li><span aria-hidden="true">✍️</span>Abstracts close {moment(conference.abstract_deadline).format("DD MMM")}</li>
                          )}
                        </ul>
                        <button
                          type="button"
                          className="mc-link"
                          onClick={() => navigate(`/${memberType}/conferences-and-events/event-details/${conference.id}/conference-details`)}
                        >
                          View conference →
                        </button>
                      </div>
                    </article>
                  );
                })}
              </Carousel>
            </section>
          )}

          <section className="mc-section">
            <div className="mc-section-head">
              <div>
                <h2>Expand your academic horizons</h2>
                <p>Support services available to every IFERP member, at member rates.</p>
              </div>
            </div>
            <Carousel label="services">
              {services.map((service) => (
                <article className="mc-card mc-service" key={service.key}>
                  <span className={`mc-service-icon mc-tint-${service.tone}`} aria-hidden="true">
                    {service.icon}
                  </span>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                  {service.href ? (
                    <a className="mc-link" href={service.href} target="_blank" rel="noopener noreferrer">
                      {service.cta} →
                    </a>
                  ) : (
                    <button type="button" className="mc-link" onClick={() => setIsSpeakerOpen(true)}>
                      {service.cta} →
                    </button>
                  )}
                </article>
              ))}
            </Carousel>
          </section>

          <section className={`mc-card mc-payment ${isPaymentOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="mc-payment-head"
              aria-expanded={isPaymentOpen}
              aria-controls="mc-payment-panel"
              onClick={() => setIsPaymentOpen((open) => !open)}
            >
              <span className="mc-payment-icon" aria-hidden="true">📄</span>
              <span className="mc-payment-title">
                <strong>Payment and plan details</strong>
                <span className="mc-payment-sub">Your plan details</span>
                <span className="mc-payment-desc">
                  Charges, GST, transaction reference and invoice. What the free tier includes, and what it doesn't
                </span>
              </span>
              <span className="mc-payment-total">
                {isFree ? "₹0 · no payment due" : `${formatMoney(payment?.total_paid, payment?.currency)} paid`}
                <span className={`mc-chevron ${isPaymentOpen ? "is-up" : ""}`} aria-hidden="true">⌄</span>
              </span>
            </button>

            {isPaymentOpen && (
              <div className="mc-payment-panel" id="mc-payment-panel">
                <div>
                  <p className="mc-label">Payment summary</p>
                  {isFree ? (
                    <dl className="mc-summary">
                      <div><dt>Plan</dt><dd>Free Membership</dd></div>
                      <div><dt>Term</dt><dd>No expiry</dd></div>
                      <div><dt>Membership fee</dt><dd>₹0.00</dd></div>
                      <div><dt>GST (18%)</dt><dd>Not applicable</dd></div>
                      <div><dt>Reference</dt><dd>{memberIdText}</dd></div>
                      <div className="mc-summary-total"><dt>Total due</dt><dd>₹0</dd></div>
                    </dl>
                  ) : (
                    <dl className="mc-summary">
                      <div><dt>Plan</dt><dd>{planLabel(plan?.plan_name)}</dd></div>
                      <div><dt>Term</dt><dd>{plan?.validity_months} months</dd></div>
                      <div><dt>Membership fee</dt><dd>{formatMoney(payment?.membership_fee, payment?.currency)}</dd></div>
                      <div>
                        <dt>{payment?.charges_label} ({payment?.charges_percent}%)</dt>
                        <dd>{formatMoney(payment?.charges_amount, payment?.currency)}</dd>
                      </div>
                      <div><dt>Payment method</dt><dd>Online payment (CC Avenue)</dd></div>
                      <div><dt>Transaction ID</dt><dd>{payment?.transaction_id || payment?.order_id || "—"}</dd></div>
                      <div className="mc-summary-total"><dt>Total paid</dt><dd>{formatMoney(payment?.total_paid, payment?.currency)}</dd></div>
                    </dl>
                  )}
                  {!isFree && (
                    <button type="button" className="mc-link mc-receipt-link" onClick={handleDownloadReceipt}>
                      {isReceiptLoading ? "Preparing receipt…" : "Download invoice (PDF) →"}
                    </button>
                  )}
                </div>
                <div>
                  <p className="mc-label">{isFree ? "What's included" : "Active from today"}</p>
                  <ul className="mc-checks">
                    {(plan?.benefits || []).map((item) => (
                      <li key={item.benefit}>
                        {item.benefit}
                        {item.value && <span className="mc-benefit-value"> — {item.value}</span>}
                      </li>
                    ))}
                  </ul>
                  {isFree && (
                    <p className="mc-muted">
                      Registration and publication discounts, unlimited repository access and the membership certificate are Premium features. You can upgrade any time from your dashboard.
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>

          {isFree && (
            <section className="mc-card mc-free">
              <div>
                <p className="mc-label mc-label-green">You're on Free Membership</p>
                <p>
                  Premium adds registration and publication discounts, unlimited repository access, your membership certificate, and priority consideration when roles open. No rush — it's here when you need it.
                </p>
              </div>
              <button type="button" className="mc-link mc-link-green" onClick={handleCompareClick}>
                Compare free and premium →
              </button>
            </section>
          )}
        </div>
      </main>

      <footer className="mc-footer">
        <div className="mc-container">
          <div className="mc-footer-top">
            <p>
              Member ID <strong>{member?.member_id || "pending verification"}</strong> · Registered {formatDate(plan?.registered_on)}
            </p>
            <ul className="mc-footer-links">
              <li>
                <button type="button" onClick={() => setContact("concierge")}>
                  Contact your Membership Concierge
                </button>
              </li>
              {!isFree && (
                <li>
                  {/* refund policy is part of the terms of service */}
                  <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Refund policy</a>
                </li>
              )}
            </ul>
          </div>
          <div className="mc-footer-bottom">
            <p>© {moment().format("YYYY")} IFERP. All rights reserved. Professional Academic Membership Platform.</p>
            <ul className="mc-footer-links">
              <li>
                <button type="button" onClick={() => setContact("support")}>Support Center</button>
              </li>
              <li>
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </li>
              <li>
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</a>
              </li>
            </ul>
          </div>
        </div>
      </footer>

      {isComparisonOpen && (
        <div className="mc-modal-backdrop" onClick={() => setIsComparisonOpen(false)}>
          <div
            className="mc-modal mc-compare"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mc-compare-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-compare-head">
              <div>
                <h3 id="mc-compare-title">Free and Premium membership</h3>
                <p>Everything in Free, plus the Premium benefits below.</p>
              </div>
              <button type="button" className="mc-compare-close" aria-label="Close" onClick={() => setIsComparisonOpen(false)}>
                ✕
              </button>
            </div>

            {isComparisonLoading && <Loader />}

            {!isComparisonLoading && comparison && (
              <>
                <div className="mc-compare-scroll">
                  <table className="mc-compare-table">
                    <thead>
                      <tr>
                        <th scope="col">Benefit</th>
                        <th scope="col">Free</th>
                        <th scope="col">Premium</th>
                      </tr>
                    </thead>
                    {comparison.categories?.map((group) => (
                      <tbody key={group.category}>
                        <tr className="mc-compare-category">
                          <th scope="colgroup" colSpan={3}>{group.category}</th>
                        </tr>
                        {group.benefits?.map((item) => (
                          <tr key={item.benefit}>
                            <td>{item.benefit}</td>
                            <td className="mc-compare-free">{item.free || "—"}</td>
                            <td className="mc-compare-premium">{item.premium || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    ))}
                  </table>
                </div>

                {comparison.plans?.length > 0 && (
                  <div className="mc-compare-plans">
                    <p className="mc-label">Premium membership</p>
                    <ul>
                      {comparison.plans.map((plan) => (
                        <li key={plan.term}>
                          <span>{plan.term}</span>
                          <strong>${plan.usd_amount}</strong>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button type="button" className="mc-btn mc-btn-primary" onClick={() => navigate(upgradePath)}>
                  Upgrade to Premium
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {contact && (
        <div className="mc-modal-backdrop" onClick={() => setContact(null)}>
          <div
            className="mc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mc-contact-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="mc-contact-title">
              {contact === "concierge" ? "Membership Concierge" : "IFERP Customer Support"}
            </h3>
            <p>
              {contact === "concierge"
                ? "Your Membership Concierge can help with your membership, benefits and renewals."
                : "Our support team can help with your account, payments and the dashboard."}
            </p>
            <dl className="mc-contact">
              <div>
                <dt>Phone</dt>
                <dd>
                  <a href={`tel:${(contact === "concierge" ? concierge : support).phone.replace(/\s/g, "")}`}>
                    {(contact === "concierge" ? concierge : support).phone}
                  </a>
                </dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${(contact === "concierge" ? concierge : support).email}`}>
                    {(contact === "concierge" ? concierge : support).email}
                  </a>
                </dd>
              </div>
            </dl>
            <button type="button" className="mc-link" onClick={() => setContact(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {isSpeakerOpen && (
        <div className="mc-modal-backdrop" onClick={() => setIsSpeakerOpen(false)}>
          <div
            className="mc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mc-speaker-title"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mc-service-icon mc-tint-blue" aria-hidden="true">🎤</span>
            <h3 id="mc-speaker-title">Become a Speaker</h3>
            <p>
              Share your published work at an IFERP conference. Write to the IFERP Speaker Bureau and the team will guide you through the next steps.
            </p>
            <a className="mc-btn mc-btn-primary" href={`mailto:${SPEAKER_BUREAU_EMAIL}`}>
              Email {SPEAKER_BUREAU_EMAIL}
            </a>
            <button type="button" className="mc-link" onClick={() => setIsSpeakerOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipConfirmed;
